import { listModelsTE } from 'api/models/listModels';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { apiErrorToReactLeft } from 'utils/errorHandling';
import { useDebounce } from 'react-use';
import { useCurrentUserId, useLoggedInUserId, useIsOrgAccount } from 'modules/Auth/hooks';
import {
  fromDataToOptionValue,
  GroupValue,
  PlatformResourceSelectorDataType,
  SectionSerializerType,
} from 'components/PlatformResourceSelector/usePlatformResourceSelector';
import { useSearchMechanism, generateOptionValueFromEntity } from 'components/PlatformResourceSelector/utils';
import { useFeaturedModels } from 'modules/Playground/api/hooks/useFeaturedModels';
import { accountLogoMap } from 'modules/Explore/utils';
import { IconCPU } from 'components/Icons';

export type ResourceType = 'models';
export type AllowedSectionsType = 'community-only' | 'me-only' | 'featured';

export const allowedSections: Record<AllowedSectionsType, string> = {
  featured: 'Featured Models',
  'me-only': 'My Models',
  'community-only': 'Community Models',
};

export type DataEntity = CF.API.Models.Model;

export type SourceDataType = Partial<Record<AllowedSectionsType, DataEntity[]>>;
export type MappedDataType = Partial<SourceDataType>;
export type IndexedDataType = Record<string, DataEntity>;

interface SourceDataStateType {
  indexed: IndexedDataType;
  labeled: SourceDataType;
}

interface ActionType {
  type: 'update';
  payload: SourceDataType;
}

const sourceDataReducer = (state: SourceDataStateType, action: ActionType): SourceDataStateType => {
  switch (action.type) {
    case 'update':
      return filterAndIndexDataEntities(state, action.payload);
    default:
      return state;
  }
};

const filterAndIndexDataEntities = (previousData: SourceDataStateType, updatedData: SourceDataType): SourceDataStateType => {
  const indexedData = { ...previousData.indexed };
  const labeledData: SourceDataType = {
    featured: [...(previousData.labeled.featured || [])],
    'me-only': [...(previousData.labeled['me-only'] || [])],
    'community-only': [...(previousData.labeled['community-only'] || [])],
  };

  Object.entries(updatedData).forEach(([key, values]) => {
    const label = key as AllowedSectionsType;
    const entries = values?.length ? values : [];
    entries.forEach((entity) => {
      const index = generateOptionValueFromEntity(entity);
      if (!indexedData[index]) {
        indexedData[index] = entity;
        labeledData[label]?.push(entity);
      }
    });
  });

  return {
    indexed: indexedData,
    labeled: labeledData,
  };
};

const initialSourceDataState: SourceDataStateType = {
  indexed: {},
  labeled: {
    featured: [],
    'me-only': [],
    'community-only': [],
  },
};

export interface ResourceSelectorDataType {
  indexed: IndexedDataType;
  mapped: MappedDataType;
  total: DataEntity[];
}

export interface ResourceSelectorDataStateType {
  data: ResourceSelectorDataType;
  isLoading: boolean;
  isSearching?: boolean;
  sectionSerializer?: SectionSerializerType;
  type: ResourceType;
}

interface UseDataForPlaygroundProps {
  customFilter?: (item: DataEntity) => boolean;
  searchParam?: string;
  fromCommunity?: boolean;
}

export const useDataForPlayground = ({
  customFilter,
  searchParam = '',
  fromCommunity = false,
}: UseDataForPlaygroundProps): ResourceSelectorDataStateType => {
  const [searchApiInput, setSearchApiInput] = useSearchMechanism(searchParam);
  const DELAY_BEFORE_SEARCHING = 350;

  // 🔎 Search API after a small delay:
  useDebounce(() => setSearchApiInput(searchParam), DELAY_BEFORE_SEARCHING, [searchParam]);

  const userId = useLoggedInUserId();
  const contextUserId = useCurrentUserId();
  const isOrg = useIsOrgAccount();
  const featuredModels = useFeaturedModels();

  const [sourceData, concatWithSourceData] = useReducer(sourceDataReducer, initialSourceDataState);

  const data = useMemo(() => {
    const indexed: IndexedDataType = sourceData.indexed;
    const mapped: MappedDataType = Object.fromEntries(Object.entries(sourceData.labeled).filter(([_, items]) => items && items.length > 0));
    const total = Object.values(indexed);
    return { indexed, mapped, total };
  }, [sourceData]);

  const [isLoading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const searchQuery = searchApiInput.length ? `&search=${searchApiInput}` : '';

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (!searchQuery.length && data.total.length) return;

    setLoading(true);

    const dataPromises: Promise<{ models: DataEntity[] }>[] = [];

    if (fromCommunity) {
      dataPromises.push(
        listModelsApi({
          abortSignal: abortController.signal,
          params: `${additionalFieldsQueryParam}${searchQuery}${trainedOnlyQueryParam}`,
        }),
      );
    }

    if (contextUserId) {
      if (userId !== contextUserId && isOrg) {
        dataPromises.push(
          listModelsApi({
            abortSignal: abortControllerRef.current.signal,
            userId: contextUserId,
            params: `${paginationQueryParam}${searchQuery}${trainedOnlyQueryParam}${additionalFieldsQueryParam}`,
          }),
        );
      } else {
        dataPromises.push(
          listModelsApi({
            abortSignal: abortControllerRef.current.signal,
            userId,
            params: `${paginationQueryParam}${searchQuery}${trainedOnlyQueryParam}${dontFetchFromMainQueryParam}`,
          }),
        );
      }
    }

    Promise.all(dataPromises)
      .then((results) => {
        let communityData: DataEntity[] = [];
        let restData: DataEntity[] = [];

        if (fromCommunity) {
          [communityData, restData] = results.map((response) => getRightResponse(response, customFilter));
        } else {
          [restData] = results.map((response) => getRightResponse(response, customFilter));
        }

        // Enrich featured models with AI logos ✨
        const featuredModelsWithIcons = featuredModels?.map((featuredModel) => {
          const ModelIcon = accountLogoMap?.[featuredModel.user_id as string];
          return {
            ...featuredModel,
            iconComponent: ModelIcon ? <ModelIcon size={24} /> : <IconCPU size={20} color="#344054" />,
            featured: true,
          };
        }) as unknown as CF.API.Models.Model[];

        return concatWithSourceData({
          type: 'update',
          payload: {
            featured: featuredModelsWithIcons,
            'me-only': restData,
            ...(fromCommunity ? { 'community-only': communityData } : {}),
          },
        });
      })
      .finally(() => setLoading(false))
      .catch(() => null);
  }, [contextUserId, isOrg, searchApiInput, userId, fromCommunity]);

  const sectionSerializer = useMemo(() => {
    const getOwnerName = () => {
      if (userId === contextUserId && !isOrg) return 'Personal';
      if (userId !== contextUserId && isOrg) return contextUserId;
      return contextUserId;
    };

    const ownerName = getOwnerName();

    return (resources: PlatformResourceSelectorDataType): GroupValue[] => {
      return Object.entries(resources.mapped).map(([key, values]) => {
        return {
          expand: false,
          label:
            key === 'featured'
              ? allowedSections.featured
              : key === 'me-only'
              ? `${ownerName}${isOrg ? `'s` : ''} models`
              : allowedSections[key as AllowedSectionsType],
          options: values.map((value) => fromDataToOptionValue(value)),
          type: 'models',
        };
      });
    };
  }, [userId, isOrg, contextUserId]);

  return {
    data,
    isLoading,
    isSearching: false, // Important: avoids grouping of search results!
    sectionSerializer,
    type: 'models',
  };
};

const getRightResponse = (response: { models?: DataEntity[] }, customFilter?: (item: DataEntity) => boolean): DataEntity[] => {
  if (!response.models) return [];
  return customFilter ? response.models.filter(customFilter) : response.models;
};

const PAGES_OF_RESULTS = 1;
const RESULTS_PER_PAGE = 25;
const paginationQueryParam = `page=${PAGES_OF_RESULTS}&per_page=${RESULTS_PER_PAGE}`;
const trainedOnlyQueryParam = `&trained_only=true`;
const additionalFieldsQueryParam = '&sort_by_modified_at=true';
const dontFetchFromMainQueryParam = '&dont_fetch_from_main=true';

type ListApiProps = {
  abortSignal: AbortSignal;
  appId?: string;
  params: string;
  userId?: string;
};

const listModelsApi = async ({ abortSignal, appId, params, userId }: ListApiProps): Promise<{ models: DataEntity[] }> => {
  return listModelsTE({ abortSignal, appId, params, userId }, apiErrorToReactLeft)().then((response) => {
    return { models: 'right' in response ? response.right.models : [] };
  });
};
